import api, { GetApiReturn, GetApiReturnError, ValidateAxiosError } from '@/modules/api.service';
import { GetDefensoria, SetDefensoria } from '@/modules/structure/defensoria/defensoria.service';
import { Endpoints } from '@/shared/constants/endpoints';
import {
  ReadDefensoriaDto,
  ReadDiretoriaDto,
  CreateDiretoriaDto,
  UpdateDiretoriaDto,
  ReadDiretoriaLotacaoDto,
  CreateDiretoriaLotacaoDto,
  UpdateDiretoriaLotacaoDto,
  UpdateDefensoriaDto,
} from '@/api.types';

function toUpdateDefensoriaPayload(data: ReadDefensoriaDto, diretoriaId: number | null): UpdateDefensoriaDto {
  return {
    id: data.id,
    ordem: data.ordem,
    nome: data.nome,
    codigo: data.codigo,
    exibirrelatorio: data.exibirrelatorio,
    relatoriosAtividadesHabilitado: data.relatoriosAtividadesHabilitado,
    maximo_estagiarios: data.maximo_estagiarios,
    anotacao: data.anotacao,
    criminal: data.criminal,
    civel: data.civel,
    predio: data.predio?.id ? { id: data.predio.id } : undefined,
    comarca: data.comarca?.id ? { id: data.comarca.id } : undefined,
    vara: data.vara?.id ? { id: data.vara.id } : undefined,
    telefone: data.telefone,
    defensoriasubstituta1: data.defensoriasubstituta1?.id ? { id: data.defensoriasubstituta1.id } : undefined,
    defensoriasubstituta2: data.defensoriasubstituta2?.id ? { id: data.defensoriasubstituta2.id } : undefined,
    diretoria: diretoriaId ? { id: diretoriaId } : undefined,
  };
}

export async function GetDiretorias(
  skip: number,
  take: number,
  nome: string | undefined = undefined,
) {
  let where = '0=0';

  if (nome) where = where += ` and diretoria.nome ilike '%${nome}%'`;

  return api
    .get(Endpoints.DIRETORIA, {
      params: { skip: skip, take: take, where: where },
    })
    .then((response) => {
      return GetApiReturn<ReadDiretoriaDto>(response.data as Array<ReadDiretoriaDto>);
    })
    .catch((error) => {
      ValidateAxiosError<ReadDiretoriaDto>(error, 'Falha ao listar diretorias.');
      return GetApiReturnError<ReadDiretoriaDto>();
    });
}

/**
 * Retorna uma diretoria de acordo com id.
 * @param {number} id
 * @returns Diretoria ou null.
 */
export async function GetDiretoria(id: number) {
  return api
    .get<ReadDiretoriaDto>(Endpoints.DIRETORIA + '/' + id)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return ValidateAxiosError<ReadDiretoriaDto>(
        error,
        'Falha ao buscar a diretoria.' + id,
      );
    });
}

/**
 * Adiciona ou altera uma diretoria, se o id for superior a 0 será um update, caso contrário uma inseração.
 * @param {CreateDiretoriaDto | UpdateDiretoriaDto} diretoria
 * @returns True se salvo, False em caso contrário.
 */
export async function SetDiretoria(diretoria: CreateDiretoriaDto | UpdateDiretoriaDto) {
  if (
    diretoria.id !== null &&
    diretoria.id != undefined &&
    diretoria.id > 0
  ) {
    return api
      .put(Endpoints.DIRETORIA + '/' + diretoria.id, diretoria as UpdateDiretoriaDto)
      .then(() => {
        return 'Diretoria atualizada.';
      })
      .catch((error) => {
        return ValidateAxiosError<string>(
          error,
          'Falha ao atualizar a diretoria.',
        );
      });
  } else {
    return api
      .post(Endpoints.DIRETORIA, diretoria as CreateDiretoriaDto)
      .then(() => {
        return 'Diretoria cadastrada.';
      })
      .catch((error) => {
        return ValidateAxiosError<string>(
          error,
          'Falha ao cadastrar a diretoria.',
        );
      });
  }
}

/**
 * Apaga uma diretoria do sistema.
 * @param {number} id
 * @returns True se apagou, False em caso contrário.
 */
export async function DeleteDiretoria(id: number) {
  return api
    .delete(Endpoints.DIRETORIA + '/' + id)
    .then(() => {
      return 'Diretoria removida.';
    })
    .catch((error) => {
      return ValidateAxiosError<string>(error, 'Falha ao remover diretoria.');
    });
}

/**
 * Busca as lotações de uma diretoria.
 * @param {number} diretoriaId
 * @returns Lista de lotações.
 */
export async function GetDiretoriaLotacoes(diretoriaId: number) {
  return api
    .get<ReadDiretoriaLotacaoDto[]>(Endpoints.DIRETORIA + '/lotacao/diretoria/' + diretoriaId)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      ValidateAxiosError<ReadDiretoriaLotacaoDto>(error, 'Falha ao buscar as lotações.');
      return [];
    });
}

/**
 * Adiciona ou altera uma lotação de diretoria.
 * @param {CreateDiretoriaLotacaoDto | UpdateDiretoriaLotacaoDto} lotacao
 * @returns Mensagem de sucesso ou erro.
 */
export async function SetDiretoriaLotacao(lotacao: CreateDiretoriaLotacaoDto | UpdateDiretoriaLotacaoDto) {
  if (
    lotacao.id !== null &&
    lotacao.id != undefined &&
    lotacao.id > 0
  ) {
    return api
      .put(Endpoints.DIRETORIA + '/lotacao/' + lotacao.id, lotacao as UpdateDiretoriaLotacaoDto)
      .then(() => {
        return 'Lotação atualizada.';
      })
      .catch((error) => {
        return ValidateAxiosError<string>(
          error,
          'Falha ao atualizar a lotação.',
        );
      });
  } else {
    return api
      .post(Endpoints.DIRETORIA + '/lotacao', lotacao as CreateDiretoriaLotacaoDto)
      .then(() => {
        return 'Lotação cadastrada.';
      })
      .catch((error) => {
        return ValidateAxiosError<string>(
          error,
          'Falha ao cadastrar a lotação.',
        );
      });
  }
}

/**
 * Apaga uma lotação de diretoria.
 * @param {number} id
 * @returns Mensagem de sucesso ou erro.
 */
export async function DeleteDiretoriaLotacao(id: number) {
  return api
    .delete(Endpoints.DIRETORIA + '/lotacao/' + id)
    .then(() => {
      return 'Lotação removida.';
    })
    .catch((error) => {
      return ValidateAxiosError<string>(error, 'Falha ao remover lotação.');
    });
}

/**
 * Busca a diretoria regional do usuário logado
 * @returns Diretoria do usuário ou null
 */
/** Lista defensorias vinculadas à diretoria regional. */
export async function GetDefensoriasPorDiretoria(diretoriaId: number) {
  const where = `diretoria.id = ${diretoriaId}`;

  return api
    .get(Endpoints.DEFENSORIA, {
      params: { skip: 0, take: 9000, where },
    })
    .then((response) => {
      return GetApiReturn<ReadDefensoriaDto>(response.data as Array<ReadDefensoriaDto>).objects;
    })
    .catch((error) => {
      ValidateAxiosError<ReadDefensoriaDto>(error, 'Falha ao listar defensorias da diretoria.');
      return [] as ReadDefensoriaDto[];
    });
}

/** Associa ou remove a defensoria desta diretoria regional. */
export async function SetDefensoriaDiretoria(defensoriaId: number, diretoriaId: number | null) {
  const defensoria = await GetDefensoria(defensoriaId);
  return SetDefensoria(toUpdateDefensoriaPayload(defensoria, diretoriaId));
}

export async function GetDiretoriaDoUsuario(): Promise<ReadDiretoriaDto | null> {
  try {
    const response = await api.get<ReadDiretoriaDto>(Endpoints.DIRETORIA + '/usuario/atual');
    return response.data;
  } catch (error) {
    // Se não encontrar, retorna null (usuário pode não ter diretoria associada)
    if ((error as any)?.response?.status === 404 || (error as any)?.response?.status === 400) {
      return null;
    }
    ValidateAxiosError<ReadDiretoriaDto>(error, 'Falha ao buscar diretoria do usuário.');
    return null;
  }
}

